/* ============================================
   usePendientesChat — cuántos mensajes sin atender tiene cada chat
   ============================================
   El contador de no leídos dice si alguien miró el chat; este dice si alguien HIZO
   algo. Son cosas distintas: el problema que motivó el triador es justamente que un
   mensaje se marca como leído y el pedido que traía se pierde igual.

   «Sin atender» lo define el backend (`GET /chat/pendientes`): entrante con intención
   accionable, sin tarea vinculada y sin respuesta posterior.
*/
import { useState, useEffect, useCallback, useRef } from 'react'
import { useRealtimeEvent } from './useRealtimeEvents'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

/** Margen para no pedir de nuevo con cada mensaje de una ráfaga. */
const DEBOUNCE_MS = 4_000

export function usePendientesChat(): ReadonlyMap<number, number> {
  const [porConversacion, setPorConversacion] = useState<Map<number, number>>(new Map())
  const timer = useRef<number | null>(null)

  const cargar = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token')
      if (!token) return

      const res = await fetch(`${API_URL}/api/chat/pendientes?solo_sin_responder=1`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return

      const data = await res.json()
      const mapa = new Map<number, number>()
      for (const item of data.items ?? []) {
        if (typeof item.conversacion_id !== 'number') continue
        mapa.set(item.conversacion_id, (mapa.get(item.conversacion_id) ?? 0) + 1)
      }
      setPorConversacion(mapa)
    } catch {
      /* el contador es contexto: si falla, el Inbox funciona igual */
    }
  }, [])

  useEffect(() => {
    void cargar()
    return () => { if (timer.current) window.clearTimeout(timer.current) }
  }, [cargar])

  // La etiqueta la pone el triador segundos después de que entra el mensaje, así que
  // se recarga con el `message_updated` que trae la clasificación, no con el entrante.
  const recargarConMargen = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => { void cargar() }, DEBOUNCE_MS)
  }, [cargar])

  useRealtimeEvent('message_updated', recargarConMargen)

  return porConversacion
}
